import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Spinner } from 'react-bootstrap';
import BottomNav from '../components/BottomNav';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import authService from '../services/authService';
import '../assets/UserAssets.css';

const UserAssets = () => {
  const [profileImage, setProfileImage] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [walletData, setWalletData] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBalance, setShowBalance] = useState(() => {
    const saved = localStorage.getItem('showBalance');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [hideSmallBalance, setHideSmallBalance] = useState(() => {
    const saved = localStorage.getItem('hideSmallBalance');
    return saved !== null ? JSON.parse(saved) : false;
  });
  // eslint-disable-next-line no-unused-vars
  const [showEarnings, setShowEarnings] = useState(() => {
    const saved = localStorage.getItem('showEarnings');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const localUser = authService.getCurrentUser();
    if (localUser) {
      setUser(localUser);
    }

    const fetchData = async () => {
      try {
        // Ensure we have a valid token
        const token = authService.getToken();
        if (!token) {
          toast.error('Please login to view your assets', {
            position: 'bottom-left',
            autoClose: 3000,
          });
          setLoading(false);
          return;
        }

        // Fetch wallet balances
        const walletResponse = await api.get('/api/trading/wallet/balances');

        if (walletResponse.data?.success && walletResponse.data?.data) {
          setWalletData(walletResponse.data.data);
        }

        // Fetch user profile from API
        const profileResponse = await api.get('/api/auth/profile');

        if (profileResponse.data?.success && profileResponse.data?.data?.user) {
          setUser(profileResponse.data.data.user);
        } else if (profileResponse.data?.user) {
          setUser(profileResponse.data.user);
        }
      } catch (error) {
        if (
          error.response?.status !== 404 &&
          error.message?.includes('wallet')
        ) {
          toast.error('Failed to load wallet data', {
            position: 'bottom-left',
            autoClose: 3000,
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    // Load profile image from API data or localStorage
    if (user?.profilePicture) {
      setProfileImage(user.profilePicture);
      localStorage.setItem('profileImage', user.profilePicture);
    } else {
      const savedImage = localStorage.getItem('profileImage');
      if (savedImage) {
        setProfileImage(savedImage);
      }
    }
  }, [user]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setProfileImage(imageUrl);

      try {
        const formData = new FormData();
        formData.append('profilePicture', file);

        await authService.updateProfile(formData);
        toast.success('Profile image updated successfully', {
          position: 'bottom-left',
          autoClose: 3000,
        });

        const profile = await authService.getProfile();
        if (profile) {
          setUser(profile);
          if (profile.profilePicture) {
            setProfileImage(profile.profilePicture);
            localStorage.setItem('profileImage', profile.profilePicture);
          }
        }
      } catch (err) {
        toast.error(err.message || 'Failed to upload profile image', {
          position: 'bottom-left',
          autoClose: 3000,
        });
        // Revert preview on error
        const savedImage = localStorage.getItem('profileImage');
        setProfileImage(savedImage);
      }
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #ffffff 0%, #ffffff 100%)',
        paddingTop: '20px',
        paddingBottom: '100px',
      }}
    >
      <Container
        fluid
        style={{
          maxWidth: isMobile ? '100%' : '85%',
          padding: 'clamp(1rem, 3vw, 1.5rem)',
        }}
      >
        {/* Profile Header with Trade History */}
        <div
          className='d-flex justify-content-between align-items-center mb-3 bg-white rounded shadow-sm p-3'
          style={{
            border: '1px solid #e0e0e0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          }}
        >
          {/* Profile Left Side */}
          <div className='d-flex align-items-center'>
            <div
              onClick={() => document.getElementById('profile-upload').click()}
              style={{ cursor: 'pointer' }}
            >
              <img
                src={profileImage || 'https://via.placeholder.com/40'}
                alt='User'
                className='rounded-circle me-2'
                style={{
                  width: 'clamp(40px, 8vw, 56px)',
                  height: 'clamp(40px, 8vw, 56px)',
                  objectFit: 'cover',
                }}
              />
              <Form.Control
                type='file'
                accept='image/*'
                id='profile-upload'
                onChange={handleImageUpload}
                style={{ display: 'none' }}
              />
            </div>
            <div>
              <div
                className='fw-bold'
                style={{ fontSize: 'clamp(0.95rem, 2.5vw, 1.1rem)' }}
              >
                {user?.fullName ||
                  `${user?.firstName || ''} ${user?.lastName || ''}`.trim() ||
                  user?.email ||
                  'User'}
              </div>
              <div
                className='text-muted'
                style={{ fontSize: 'clamp(0.75rem, 2vw, 0.85rem)' }}
              >
                ID: {user?.uid || user?.id || 'N/A'}
              </div>
              <span
                className={`badge ${
                  user?.kycStatus === 'APPROVED' ? 'bg-success' : 'bg-secondary'
                }`}
                style={{
                  fontSize: 'clamp(0.65rem, 1.8vw, 0.75rem)',
                  marginTop: '4px',
                }}
              >
                {user?.kycStatus === 'APPROVED' ? 'Verified' : 'Unverified'}
              </span>
            </div>
          </div>

          {/* Trade History Button */}
          <Link
            to='/history'
            className='btn btn-outline-primary'
            style={{
              fontSize: 'clamp(0.75rem, 2vw, 0.9rem)',
              padding:
                'clamp(0.4rem, 1.5vw, 0.6rem) clamp(0.8rem, 2.5vw, 1rem)',
              borderRadius: '8px',
              fontWeight: '600',
            }}
          >
            Trade History
          </Link>
        </div>

        {/* Asset Summary */}
        <Row className='mb-3 gx-2'>
          <Col xs={12}>
            <div
              className='bg-white rounded shadow-sm p-3'
              style={{
                border: '1px solid #e0e0e0',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              }}
            >
              <div
                className='d-flex align-items-center mb-2'
                style={{ gap: '8px' }}
              >
                <div
                  className='text-muted'
                  style={{ fontSize: 'clamp(0.8rem, 2vw, 0.85rem)' }}
                >
                  Total assets (USDT)
                </div>
                <i
                  className={`bi ${showBalance ? 'bi-eye' : 'bi-eye-slash'}`}
                  onClick={() => {
                    const newValue = !showBalance;
                    setShowBalance(newValue);
                    localStorage.setItem(
                      'showBalance',
                      JSON.stringify(newValue)
                    );
                  }}
                  style={{
                    fontSize: 'clamp(1rem, 2.5vw, 1.2rem)',
                    color: '#6c757d',
                    cursor: 'pointer',
                  }}
                ></i>
              </div>
              <div
                className='fw-bold'
                style={{
                  fontSize: 'clamp(1.1rem, 3.5vw, 1.5rem)',
                  color: '#1a1a1a',
                }}
              >
                {loading ? (
                  <Spinner animation='border' size='sm' />
                ) : showBalance ? (
                  `$${
                    walletData?.total?.realBalanceUSD?.toLocaleString() ||
                    '0.00'
                  }`
                ) : (
                  '********'
                )}
              </div>
            </div>
          </Col>
        </Row>

        {/* Gradient Earnings Bar (matches screenshot design) */}
        <div
          className='rounded shadow-sm mb-3'
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#ffffff',
            boxShadow: '0 4px 12px rgba(102,126,234,0.18)',
            padding: 'clamp(0.75rem, 2.5vw, 1rem)',
          }}
        >
          <Row className='g-0 align-items-center'>
            <Col xs={6} className='text-center'>
              <div
                style={{
                  fontSize: 'clamp(0.85rem, 2vw, 0.95rem)',
                  opacity: 0.95,
                }}
              >
                Earnings today
              </div>
              <div
                className='fw-bold'
                style={{
                  fontSize: 'clamp(0.95rem, 2.5vw, 1.35rem)',
                  marginTop: '6px',
                }}
              >
                {loading ? (
                  <Spinner animation='border' size='sm' variant='light' />
                ) : showEarnings ? (
                  `$${(walletData?.todaysEarnings || 0).toLocaleString(
                    undefined,
                    { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                  )}`
                ) : (
                  '********'
                )}
              </div>
            </Col>

            <Col
              xs={6}
              className='text-center'
              style={{ borderLeft: '1px solid rgba(255,255,255,0.12)' }}
            >
              <div
                style={{
                  fontSize: 'clamp(0.85rem, 2vw, 0.95rem)',
                  opacity: 0.95,
                }}
              >
                Historical earnings
              </div>
              <div
                className='fw-bold'
                style={{
                  fontSize: 'clamp(0.95rem, 2.5vw, 1.35rem)',
                  marginTop: '6px',
                }}
              >
                {loading ? (
                  <Spinner animation='border' size='sm' variant='light' />
                ) : (
                  `$${(walletData?.historicalEarnings30Sum || 0).toLocaleString(
                    undefined,
                    {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }
                  )}`
                )}
              </div>
            </Col>
          </Row>
        </div>

        {/* Action Buttons */}
        <div
          className='bg-white rounded shadow-sm p-3 d-flex justify-content-around mb-3'
          style={{
            border: '1px solid #e0e0e0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          }}
        >
          <Link to='/deposit' className='text-center text-decoration-none'>
            <div
              style={{
                cursor: 'pointer',
                transition: 'transform 0.2s',
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.transform = 'translateY(-2px)')
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.transform = 'translateY(0)')
              }
            >
              <i
                className='bi bi-wallet2'
                style={{
                  fontSize: 'clamp(1.5rem, 5vw, 2rem)',
                  color: '#667eea',
                }}
              ></i>
              <div
                style={{
                  fontSize: 'clamp(0.75rem, 2vw, 0.85rem)',
                  color: '#333',
                  marginTop: '4px',
                  fontWeight: '500',
                }}
              >
                Deposit
              </div>
            </div>
          </Link>
          <Link
            to='/exchangeassets'
            className='text-center text-decoration-none'
          >
            <div
              style={{
                cursor: 'pointer',
                transition: 'transform 0.2s',
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.transform = 'translateY(-2px)')
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.transform = 'translateY(0)')
              }
            >
              <i
                className='bi bi-currency-exchange'
                style={{
                  fontSize: 'clamp(1.5rem, 5vw, 2rem)',
                  color: '#667eea',
                }}
              ></i>
              <div
                style={{
                  fontSize: 'clamp(0.75rem, 2vw, 0.85rem)',
                  color: '#333',
                  marginTop: '4px',
                  fontWeight: '500',
                }}
              >
                Exchange
              </div>
            </div>
          </Link>
          <Link to='/withdraw' className='text-center text-decoration-none'>
            <div
              style={{
                cursor: 'pointer',
                transition: 'transform 0.2s',
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.transform = 'translateY(-2px)')
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.transform = 'translateY(0)')
              }
            >
              <i
                className='bi bi-cash-stack'
                style={{
                  fontSize: 'clamp(1.5rem, 5vw, 2rem)',
                  color: '#667eea',
                }}
              ></i>
              <div
                style={{
                  fontSize: 'clamp(0.75rem, 2vw, 0.85rem)',
                  color: '#333',
                  marginTop: '4px',
                  fontWeight: '500',
                }}
              >
                Withdraw
              </div>
            </div>
          </Link>
        </div>

        {/* Search Bar */}
        <div className='position-relative mb-3'>
          <i
            className='bi bi-search position-absolute'
            style={{
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#6c757d',
              fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)',
            }}
          ></i>
          <Form.Control
            type='text'
            placeholder='Search'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              paddingLeft: '40px',
              borderRadius: '10px',
              border: '1px solid #e0e0e0',
              fontSize: 'clamp(0.85rem, 2.5vw, 1rem)',
              padding:
                'clamp(0.6rem, 2vw, 0.75rem) clamp(0.6rem, 2vw, 0.75rem) clamp(0.6rem, 2vw, 0.75rem) 40px',
            }}
          />
        </div>

        {/* Hide Balance Toggle */}
        <div
          className='d-flex justify-content-between align-items-center mb-3 bg-white rounded shadow-sm p-3'
          style={{
            border: '1px solid #e0e0e0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          }}
        >
          <span
            className='fw-semibold'
            style={{
              fontSize: 'clamp(0.85rem, 2.5vw, 1rem)',
              color: '#333',
            }}
          >
            Hide small balance
          </span>
          <Form.Check
            type='switch'
            id='hide-balance'
            checked={hideSmallBalance}
            onChange={(e) => {
              const newValue = e.target.checked;
              setHideSmallBalance(newValue);
              localStorage.setItem(
                'hideSmallBalance',
                JSON.stringify(newValue)
              );
            }}
          />
        </div>

        {/* Asset List */}
        {walletData?.real ? (
          Object.entries(walletData.real)
            .filter(([symbol]) =>
              symbol.toLowerCase().includes(searchQuery.toLowerCase())
            )
            .map(([symbol, coinData]) => (
              <div
                key={symbol}
                className='bg-white rounded shadow-sm p-3 mb-3'
                style={{
                  border: '1px solid #e0e0e0',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                }}
              >
                <div
                  className='fw-bold mb-3'
                  style={{
                    fontSize: 'clamp(1rem, 2.8vw, 1.2rem)',
                    color: '#1a1a1a',
                  }}
                >
                  {symbol}
                </div>
                <Row className='g-2'>
                  <Col xs={6}>
                    <div
                      className='text-muted'
                      style={{ fontSize: 'clamp(0.75rem, 2vw, 0.85rem)' }}
                    >
                      Available assets
                    </div>
                    <div
                      className='fw-semibold'
                      style={{ fontSize: 'clamp(0.85rem, 2.2vw, 0.95rem)' }}
                    >
                      {hideSmallBalance
                        ? '******'
                        : (coinData.availableAssets || 0).toFixed(2)}
                    </div>
                  </Col>
                  <Col xs={6}>
                    <div
                      className='text-muted'
                      style={{ fontSize: 'clamp(0.75rem, 2vw, 0.85rem)' }}
                    >
                      Lock
                    </div>
                    <div
                      className='fw-semibold'
                      style={{ fontSize: 'clamp(0.85rem, 2.2vw, 0.95rem)' }}
                    >
                      {hideSmallBalance
                        ? '******'
                        : (coinData.locked || 0).toFixed(2)}
                    </div>
                  </Col>
                  <Col xs={6}>
                    <div
                      className='text-muted'
                      style={{ fontSize: 'clamp(0.75rem, 2vw, 0.85rem)' }}
                    >
                      Waiting
                    </div>
                    <div
                      className='fw-semibold'
                      style={{ fontSize: 'clamp(0.85rem, 2.2vw, 0.95rem)' }}
                    >
                      {hideSmallBalance
                        ? '******'
                        : (coinData.waiting || 0).toFixed(2)}
                    </div>
                  </Col>
                  <Col xs={6}>
                    <div
                      className='text-muted'
                      style={{ fontSize: 'clamp(0.75rem, 2vw, 0.85rem)' }}
                    >
                      Assets to be released
                    </div>
                    <div
                      className='fw-semibold'
                      style={{ fontSize: 'clamp(0.85rem, 2.2vw, 0.95rem)' }}
                    >
                      {hideSmallBalance
                        ? '******'
                        : (coinData.assetsToBeReleased || 0).toFixed(2)}
                    </div>
                  </Col>
                </Row>
              </div>
            ))
        ) : (
          <div className='text-center py-4'>
            <Spinner animation='border' variant='primary' />
            <p className='mt-2 text-muted'>Loading assets...</p>
          </div>
        )}
      </Container>

      <BottomNav />
    </div>
  );
};

export default UserAssets;
